/**
 * safety-signal.ts — categorical safety read derived from the existing
 * incident feed. No new APIs, no new datasets, no fake values.
 *
 * Why this exists:
 *   The verdict card and the point-evaluator panel previously surfaced a
 *   numeric "Safety: X.X / 10" bar. Under the current incident feed
 *   (≈10 cautionary items city-wide for HYD, ≈6 for MEL) most 1-km
 *   neighbourhoods contain zero items and the formula collapsed to
 *   10/10 — every spot looked perfectly safe. Buyers reported this as
 *   false trust that invalidated their decisions.
 *
 *   This module replaces the score with a categorical signal:
 *
 *     SAFE      → 0 cautionary incidents within 1 km
 *     MODERATE  → 1 cautionary incident within 1 km
 *     RISKY     → 2 or more cautionary incidents within 1 km
 *
 *   The thresholds are tuned to the feed density we actually have.
 *   With 6–10 items city-wide a 1-km window typically holds 0–2 — three
 *   buckets at [0, 1, 2+] is the most discriminating split that doesn't
 *   pretend to a precision the data can't support.
 *
 * Cross-source deduplication (added 2026-04-27):
 *   The aggregated feed splices news from multiple syndicators, so a
 *   single real-world event lands as 2-4 separate items (Dailyhunt,
 *   The Hindu, Telangana Today all republish the same Gachibowli fire
 *   story under their own headline). Counting each report inflated the
 *   per-suburb count and pushed quiet suburbs into "risky".
 *
 *   We now run the union of `items + extras` through
 *   `deduplicateIncidents()` (see src/data/incident-dedup.ts) BEFORE
 *   the haversine-and-count step. After dedup, `count` reflects
 *   distinct events; `reportCount` reflects total reports across
 *   sources, surfaced in the verdict card as "X incidents (from Y
 *   reports across sources)" so the user understands when a count of
 *   1 was actually corroborated by 3 publishers.
 *
 *   Why include extras in dedup input: the `extras` array is the
 *   relaxed-cap supplement used by the near-me panel — it's where most
 *   cross-source duplicates live. Folding extras in lets dedup spot
 *   them; afterwards `count` reflects unique events regardless of
 *   which array they came from.
 *
 * Honesty contract — read before extending:
 *   1. Pure derivation. No score change anywhere — the verdict colour,
 *      `confidenceScore`, and lens-mode interpretations are NOT a
 *      function of safety level. This signal is descriptive.
 *   2. We use COUNT, not severity-weighted sum. The existing buyer-
 *      evaluator weighted severity, but with a feed this thin the
 *      severity weight produced bizarre swings (a single severity-5
 *      incident cratered the score, a single severity-1 incident
 *      barely moved it). Three buckets on raw count are honest and
 *      legible.
 *   3. Cautionary category only. Positive-news items in the feed (e.g.
 *      "park reopens") don't contribute to safety perception in either
 *      direction; counting them would muddle the meaning.
 *   4. Coverage caveat: the feed is curated and recent — typically the
 *      last 30 days of news. SAFE means "no cautionary items reported
 *      near here in our feed", NOT "this place is statistically safe".
 *      The card surfaces this in user-facing copy via `coverageNote()`.
 *   5. One real-world event = one count. Cross-source duplicates are
 *      collapsed via `deduplicateIncidents()` before we count.
 *   6. Async load. Incident JSON sits in /api/incidents-{city}.json,
 *      same path the buyer-evaluator already prefetches. We share the
 *      browser HTTP cache (cache: "force-cache") so calling init twice
 *      across the two modules costs one round-trip in practice.
 */

import type { Verdict } from "./verdicts";
import { type DedupedIncident } from "./incident-dedup";
// Single shared loader — see src/data/incidents-loader.ts. This module
// no longer owns its own fetch; it pulls from the same cached deduped
// feed every other UI surface uses, which is what guarantees the
// verdict card, the click-card, the map bubbles, and the near-me sheet
// all reflect identical event counts for the same city.
import { loadIncidents, type CityKey as LoaderCityKey } from "./incidents-loader";

export type SafetyLevel = "safe" | "moderate" | "risky";

export interface SafetySignal {
  /**
   * Distinct cautionary EVENTS within 1 km of the verdict centroid,
   * after cross-source duplicates have been collapsed. This is the
   * number that drives the SAFE / MODERATE / RISKY bucket.
   */
  count: number;
  /**
   * Total raw reports across all sources for the events in `count`.
   * Equal to `count` when no dedup happened; strictly greater when
   * one or more events were corroborated by multiple sources.
   * Surfaced in the verdict card as "X incidents (from Y reports
   * across sources)" when reportCount > count.
   */
  reportCount: number;
  /** Distinct source names contributing to the events in this 1 km window. */
  mergedSources: string[];
  /**
   * True if any of the events within 1 km were reported by more than
   * one source (i.e. the dedup actually collapsed something here).
   * Drives the "Multiple reports of same event merged" hint in the UI.
   */
  hasMergedReports: boolean;
  level: SafetyLevel;
  /** True once the incident feed has been loaded for the active city. */
  ready: boolean;
}

/* -------------------------------------------------------------------------- */
/*  State — keyed by city so a HYD ↔ MEL switch doesn't poison the map.       */
/* -------------------------------------------------------------------------- */

// Re-export the loader's CityKey so callers don't have to import from
// two places. Same string union, just shared.
export type CityKey = LoaderCityKey;

interface PerSuburbStats {
  /** Distinct events (deduped clusters) within 1 km. */
  count: number;
  /** Sum of reportCount across those clusters. */
  reportCount: number;
  /** Distinct source names across those clusters. */
  mergedSources: string[];
  /** True iff at least one cluster in range had reportCount > 1. */
  hasMergedReports: boolean;
}

const _statsByCity: Map<CityKey, Map<string, PerSuburbStats>> = new Map();
const _dedupedByCity: Map<CityKey, DedupedIncident[]> = new Map();
const _readyByCity: Map<CityKey, boolean> = new Map();
const _initInFlight: Map<CityKey, Promise<void>> = new Map();

const RADIUS_METERS = 1000;

/* -------------------------------------------------------------------------- */
/*  Distance — local copy of haversine to avoid coupling to buyer-evaluator. */
/*  This file is in src/data/ which is upstream of src/ui/, so we cannot     */
/*  import from a UI module without inverting the dependency graph.          */
/* -------------------------------------------------------------------------- */

function haversineMeters(
  lng1: number, lat1: number, lng2: number, lat2: number
): number {
  const R = 6371000; // metres
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/* -------------------------------------------------------------------------- */
/*  Init — fetch incidents, count per verdict, cache.                         */
/* -------------------------------------------------------------------------- */

export interface InitSafetyOptions {
  city: CityKey;
  verdicts: ReadonlyArray<Verdict>;
}

/**
 * Eagerly load incident JSON for `city` and precompute per-suburb counts.
 *
 * Called by main-simple.ts during boot, once per city. Idempotent — a
 * second call for the same city while the first is in flight returns
 * the existing promise.
 *
 * Network failure is non-fatal: we leave the city's count map empty and
 * `ready=false`. Callers will see `level=safe, ready=false` and the
 * verdict-card UI surfaces "(checking…)" copy in that branch so the
 * user knows the absence of a signal isn't a positive signal.
 */
export function initSafetySignal(opts: InitSafetyOptions): Promise<void> {
  const { city, verdicts } = opts;
  if (_readyByCity.get(city)) return Promise.resolve();
  const inFlight = _initInFlight.get(city);
  if (inFlight) return inFlight;

  const p = (async () => {
    // Read from the SHARED loader. This module no longer owns the fetch
    // — the loader hands back an already-deduplicated feed shared with
    // every other UI surface, so a verdict-card "1 incident" lookup
    // here always matches the bubble layer / near-me sheet for the same
    // suburb. If the loader returns ready:false (network failure), we
    // leave _readyByCity unset and callers see the placeholder branch
    // in `getSafety`.
    const feed = await loadIncidents(city);
    if (!feed.ready) return;

    // We only count cautionary events for the safety bucket. Positive
    // items (festivals, openings) don't contribute to safety perception.
    const cautionary = feed.dedupedCautionary;

    // Per-suburb stats: for each verdict, find deduped events whose
    // representative coord sits within `RADIUS_METERS` of the verdict
    // centroid, and aggregate count + report-count + sources.
    // Cost: O(verdicts × deduped). With ~100 verdicts × ~15 deduped
    // events that's < 2 ms total.
    const stats = new Map<string, PerSuburbStats>();
    for (const v of verdicts) {
      let count = 0;
      let reportCount = 0;
      const sources = new Set<string>();
      let hasMergedReports = false;
      for (const ev of cautionary) {
        const d = haversineMeters(
          ev.coord[0], ev.coord[1], v.coord[0], v.coord[1]
        );
        if (d > RADIUS_METERS) continue;
        count += 1;
        reportCount += ev.reportCount;
        for (const s of ev.mergedSources) sources.add(s);
        if (ev.reportCount > 1) hasMergedReports = true;
      }
      stats.set(v.id, {
        count,
        reportCount,
        mergedSources: Array.from(sources),
        hasMergedReports,
      });
    }
    _statsByCity.set(city, stats);
    _dedupedByCity.set(city, [...cautionary]);
    _readyByCity.set(city, true);
  })();
  _initInFlight.set(city, p);
  return p;
}

/** Resolved when init for `city` has either completed or failed. */
export function whenSafetyReady(city: CityKey): Promise<void> {
  return _initInFlight.get(city) ?? Promise.resolve();
}

/* -------------------------------------------------------------------------- */
/*  Lookup                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Bucket a per-window incident count into the canonical safety level.
 *
 * Exported so any consumer that already has a count (e.g. the click-card
 * in buyer-evaluator, which counts events within 1 km of an arbitrary
 * lng/lat — not a suburb id — and therefore can't go through `getSafety`)
 * uses the EXACT same thresholds as the verdict card. This is the single
 * source of truth for "how many events crosses into Moderate / High".
 *
 *   0  → "safe"     (Low activity)
 *   1  → "moderate" (Moderate activity)
 *   2+ → "risky"    (High activity)
 */
export function levelFromCount(count: number): SafetyLevel {
  if (count >= 2) return "risky";
  if (count === 1) return "moderate";
  return "safe";
}

/**
 * Lookup safety for a suburb id.
 *
 * If init hasn't resolved for the verdict's city, returns a placeholder
 * `{ count: 0, reportCount: 0, mergedSources: [], hasMergedReports: false,
 *    level: "safe", ready: false }`. Callers MUST check `ready` and
 * render appropriate copy — `ready=false` is NOT a positive safety
 * signal, just a "we don't know yet" marker.
 *
 * If the verdict id isn't recognised (unlikely — we precomputed for the
 * full bank), behaves the same as ready=false.
 */
export function getSafety(suburbId: string, city: CityKey): SafetySignal {
  const stats = _statsByCity.get(city);
  const ready = !!_readyByCity.get(city);
  if (!stats || !ready) {
    return {
      count: 0,
      reportCount: 0,
      mergedSources: [],
      hasMergedReports: false,
      level: "safe",
      ready: false,
    };
  }
  const s = stats.get(suburbId);
  if (!s) {
    return {
      count: 0,
      reportCount: 0,
      mergedSources: [],
      hasMergedReports: false,
      level: "safe",
      ready: true,
    };
  }
  return {
    count: s.count,
    reportCount: s.reportCount,
    mergedSources: s.mergedSources,
    hasMergedReports: s.hasMergedReports,
    level: levelFromCount(s.count),
    ready: true,
  };
}

/**
 * Returns the canonical deduplicated cautionary feed for the active
 * city, or an empty array when init hasn't resolved.
 *
 * Provided for the buyer-evaluator point-card, which counts incidents
 * within 1 km of a user-clicked map point (NOT a suburb centroid). It
 * needs the raw deduped list, not the per-suburb pre-aggregation. By
 * sharing this list across modules we guarantee the verdict card and
 * the point card report the same event-count for the same window.
 */
export function getDedupedCautionary(city: CityKey): ReadonlyArray<DedupedIncident> {
  return _dedupedByCity.get(city) ?? [];
}

/* -------------------------------------------------------------------------- */
/*  User-facing copy                                                          */
/* -------------------------------------------------------------------------- */

/** Inline label for "Safety: ___ incident activity" rows. */
export function safetyActivityLabel(level: SafetyLevel): string {
  switch (level) {
    case "safe":     return "Low";
    case "moderate": return "Mixed";
    case "risky":    return "High";
  }
}

/** One-line hint for the safety metadata row in the verdict card. */
export function safetyHint(level: SafetyLevel): string {
  switch (level) {
    case "safe":
      return "No cautionary items reported within 1 km in our feed";
    case "moderate":
      return "One cautionary item reported within 1 km — read details";
    case "risky":
      return "Multiple cautionary items reported within 1 km — read details";
  }
}

/** Bullet copy appended to verdict reasons. */
export function safetyReasonBullet(level: SafetyLevel): string {
  switch (level) {
    case "safe":     return "Low incident activity nearby";
    case "moderate": return "Mixed incident activity nearby";
    case "risky":    return "Frequent incidents reported nearby";
  }
}

/** Coverage caveat. The feed is curated news from the last few weeks,
 *  not a crime statistics dataset — surface the limitation in copy. */
export function safetyCoverageNote(): string {
  return "Counted from our recent-incidents feed; not a crime-statistics dataset.";
}

/**
 * Compose the per-suburb count line shown in the verdict card.
 *
 *   - When dedup didn't collapse anything across distinct sources:
 *       "3 incidents within 1 km"
 *   - When dedup merged cross-source duplicates:
 *       "3 incidents within 1 km (reported by 5 sources)"
 *
 * `count === 0` returns the "no items" phrasing. We count distinct
 * EVENTS (after the Jaccard+window dedup pass) and disclose the
 * source-corroboration breadth — that's the number a reader actually
 * cares about ("how many independent sources saw this?") rather than
 * raw report count, which inflates numbers without adding evidence.
 */
export function safetyCountLine(opts: {
  count: number;
  sourceCount: number;
}): string {
  const { count, sourceCount } = opts;
  if (count === 0) return "No cautionary items reported within 1 km";
  const noun = count === 1 ? "incident" : "incidents";
  const base = `${count} ${noun} within 1 km`;
  if (sourceCount > count) {
    const sourceNoun = sourceCount === 1 ? "source" : "sources";
    return `${base} (reported by ${sourceCount} ${sourceNoun})`;
  }
  return base;
}

/**
 * Optional small hint shown only when the suburb has at least one
 * event corroborated by multiple sources. Empty string when the user
 * doesn't need to see it (no merges happened).
 */
export function safetyMergedHint(hasMergedReports: boolean): string {
  return hasMergedReports ? "Multiple reports of same event merged" : "";
}

/**
 * Graded data-confidence interpretation. Pure count → text mapping with
 * no logic, no scoring, no analysis — exactly the four tiers fixed by
 * product. Used by the verdict card / right panel beneath the safety
 * chip so the user understands *how much* the signal can be trusted
 * (data sufficiency) separately from *what* the signal says (level).
 *
 * Tiers:
 *   0–2   → "Not enough recent data"
 *   3–7   → "Limited recent reports — signals may be incomplete"
 *   8–20  → "Some recent activity — monitor patterns"
 *   21+   → "Multiple recent incidents — clear pattern emerging"
 */
export function safetyDataConfidence(count: number): string {
  if (count <= 2)  return "Not enough recent data";
  if (count <= 7)  return "Limited recent reports — signals may be incomplete";
  if (count <= 20) return "Some recent activity — monitor patterns";
  return "Multiple recent incidents — clear pattern emerging";
}
